import autobind from 'autobind-decorator';
import * as React from 'react';
import onClickOutside from 'react-onclickoutside';

import { OpenArrowButton } from '../../active-button';
import { Text } from '../../texts';
import Waves from '../../waves';
import { DropdownList, TextItem } from '../dropdown-list';
import { Styled } from '../styled';
import { OptionType } from './interfaces';
import { DropdownInputOptionsTypeguards } from './option-typeguard';


interface Props <T extends OptionType>{
  options: T[];
  selectedOption: string | number;
  maxItemsAmount: number;
  tooltips: any[];
  onClick: (value: T) => void;

  optionRenderer?: (option: T, isActive: boolean) => React.ReactNode;
  selectedOptionRenderer?: (option: T) => React.ReactNode;
  title?: string;
  disabled?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
  closeAfterClick?: boolean;
  afterClick?: () => void;
}

interface State {
  isDropdownOpen: boolean;
}

class DropdownInputComponent<T extends OptionType = OptionType> extends React.PureComponent<Props<T>, State> {

  constructor(props: Props<T>) {
    super(props);
    this.state = { isDropdownOpen: false };
  }

  public render(): React.ReactNode {
    const {
      title,
      options,
      disabled,
      backgroundColor,
      borderRadius,
      tooltips,
      maxItemsAmount = 4,
    } = this.props;
    const activeOptionIndex = options.findIndex(this.isOptionSelected);
    const activeOption = options[activeOptionIndex];

    return (
      <Styled.DropDownContainer
        borderRadius={borderRadius}
        disabled={disabled}
        backgroundColor={backgroundColor}
        withHeader={title}
        isDropdownOpen={this.state.isDropdownOpen}
        onClick={this.toggleDropDawnOpen}
      >
        <Styled.SelectedContent borderRadius={borderRadius}>
          <Styled.TextsContainer withHeader={title}>
            {title && <Styled.Header value={true}>{title}</Styled.Header>}
            {this.renderSelectedOption(activeOption)}
          </Styled.TextsContainer>
          <OpenArrowButton isOpen={this.state.isDropdownOpen} />
        </Styled.SelectedContent>
        <Waves />
        {this.state.isDropdownOpen && (
          <DropdownList
            borderRadius={borderRadius}
            filteredElements={options}
            activeElements={[activeOptionIndex]}
            activeElementIndex={activeOptionIndex}
            maxItemsAmount={maxItemsAmount}
            tooltips={tooltips}
            onClick={this.selectFromDropDawn}
            optionRenderer={this.renderOption}
          />
        )}
      </Styled.DropDownContainer>
    );
  }

  public handleClickOutside(): void {
    this.setState({ isDropdownOpen: false });
  }

  @autobind
  private isOptionSelected(option: T): boolean {
    return DropdownInputOptionsTypeguards.isBaseOption(option)
      ? option.value === this.props.selectedOption
      : option === this.props.selectedOption;
  }

  @autobind
  private renderSelectedOption(option: T): React.ReactNode {
    return this.props.selectedOptionRenderer
      ? this.props.selectedOptionRenderer(option)
      : this.defaultSelectionRenderer(option);
  }

  @autobind
  private renderOption(option: T, isActive: boolean): React.ReactNode {
    return this.props.optionRenderer
      ? this.props.optionRenderer(option, isActive)
      : this.defaultOptionRenderer(option, isActive);
  }

  private defaultSelectionRenderer(option: OptionType): React.ReactNode {
    const title = DropdownInputOptionsTypeguards.isBaseOption(option) ? option.value : option;
    return <Text>{title}</Text>;
  }

  private defaultOptionRenderer(option: OptionType, isActive: boolean): React.ReactNode {
    const title = DropdownInputOptionsTypeguards.isBaseOption(option) ? option.value : option;
    return (
      <TextItem isActive={isActive}>
        {title}
      </TextItem>
    );
  }

  @autobind
  private toggleDropDawnOpen(e: any): void {
    this.setState({ isDropdownOpen: !this.state.isDropdownOpen });
    e.stopPropagation();
  }

  @autobind
  private selectFromDropDawn(event: React.MouseEvent, index: number): void {
    const {
      options,
      onClick,
      afterClick = () => {},
      closeAfterClick = false,
    } = this.props;
    onClick(options[index]);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    closeAfterClick && this.setState({ isDropdownOpen: false });
    afterClick();
    event.stopPropagation();
  }
}

export const DropdownInputNew = onClickOutside(DropdownInputComponent);
